import React from "react";
import LogoWaitemata from "assets/logo-waitemata.png";
import LogoCounties from "assets/logo-counties.jpg";
import LogoNorthland from "assets/logo-northland.jpeg";
import { Layout as AntdLayout, Row, Col, Menu, Avatar, Tooltip, Space } from "antd";
import { useMsal } from "@azure/msal-react";
import { Link } from "react-router-dom";
import {
	DashboardOutlined,
	ContainerOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { SignOutButton } from "./SignOutButton";
import { SignInButton } from "./SignInButton";
import { useSelector } from "react-redux";
import { selectUser } from "../reducers/securitySlice";
import FormHeader from "../features/cme/Components/FormHeader";
import { selectGlobal } from "reducers/global";
import { ORGS } from "constants.js";

const {
	Header: AntdHeader,
	Content: AntdContent,
	Footer: AntdFooter,
	Sider,
} = AntdLayout;
const { SubMenu } = Menu;

const Logo = () => {
	const user = useSelector(selectUser);
	return (
		<div className="qna-page-header-logo-only">
			{user?.organisation === ORGS.COUNTIES_MANUKAU ? (
				<img src={LogoCounties} alt="Company logo"></img>
			) : user?.organisation === "WDHB" ||
			  user?.organisation === "Waitemata" ? (
				<img src={LogoWaitemata} alt="Company logo"></img>
			) : user?.organisation === ORGS.NORTHLAND ? (
				<img src={LogoNorthland} alt="Company logo"></img>
			) : (
				<></>
			)}{" "}
		</div>
	);
};

const Title = ({ title }) => {
	return title ? (
		<div style={{ overflow: "hidden" }}>
			<h1 className="qna-header-title qna-header-title-small">
				{title}{" "}
				{process.env.REACT_APP_IS_PRODUCTION !== "true" ? (
					<span style={{ color: "red" }}>TEST</span>
				) : null}
			</h1>
		</div>
	) : (
		<></>
	);
};

const UserAvatar = () => {
	const user = useSelector(selectUser);
	const { instance } = useMsal();
	//fall back to the signed in azure account, so the avatar (and the email
	//it displays) is still available when the user is unknown to InForm
	const { name, username } =
		instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? {};

	const fullName = user?.id
		? `${user.firstName} ${user.lastName}`
		: name ?? "";
	const email = user?.id ? user.email : username;
	const initials = fullName
		.split(" ")
		.filter((n) => n)
		.map((n) => n.substring(0, 1))
		.slice(0, 2)
		.join("");

	if (!fullName && !email) return <SignInButton />;

	return (
		<Tooltip
			title={
				<>
					<div>Signed in User: {fullName}</div>
					{email ? <div>{email}</div> : null}
				</>
			}
		>
			<Avatar
				className="qna-header-avatar"
				style={{ backgroundColor: "#15284C", color: "white" }}
			>
				{initials}
			</Avatar>
		</Tooltip>
	);
};

export const Header = ({ showCmeFormHeader = true }) => {
	const { screenTitle, showHeader } = useSelector(selectGlobal);

	React.useEffect(() => {
		document.title = screenTitle;
	}, [screenTitle]);

	return showHeader ? (
		<AntdHeader
			className="qna-header-container"
			style={showCmeFormHeader ? { height: 85 } : { height: 50 }}
		>
			<Row
				justify="space-between"
				style={{
					height: 50,
					backgroundColor: "white",
					lineHeight: "40px",
					alignItems: "center",
				}}
			>
				<Col
					span={20}
					style={{ display: "flex", alignItems: "center" }}
				>
					<Logo />
					<Title title={screenTitle} />
				</Col>
				<Col span={4} className="qna-header-user">
					<Space size="small">
						<UserAvatar />
						{/* the cme form header row below carries its own sign out
						button — only show one here when that row is hidden */}
						{!showCmeFormHeader && <SignOutButton />}
					</Space>
				</Col>
			</Row>
			{showCmeFormHeader && (
				<Row
					justify="space-between"
					style={{
						height: 35,
						lineHeight: "35px",
						borderBottom: "1px solid #ddd",
						background: "#15284c",
						paddingLeft: 10,
					}}
					wrap={false}
				>
					<Col flex="auto">
						<FormHeader />
					</Col>
					<Col flex="70px">
						<SignOutButton />
					</Col>
				</Row>
			)}
		</AntdHeader>
	) : (
		<></>
	);
};

export const Content = ({ children, style }) => {
	return (
		<AntdContent
			style={style}
			className="site-content qna-content-container"
		>
			{children}
		</AntdContent>
	);
};

export const Layout = ({ children }) => {
	return <AntdLayout className="qna-container">{children}</AntdLayout>;
};

export const Footer = ({ style, children }) => {
	return (
		<AntdFooter style={style} className="qna-footer-container print-hide">
			{children}
		</AntdFooter>
	);
};

export const FullLayout = ({ children, title }) => {
	//test only
	return (
		<AntdLayout className="qna-container">
			<Header title={title} />
			<AntdLayout>
				<Sider
					className="qna-sider"
					width={200}
					breakpoint="xl"
					collapsedWidth="50"
				>
					<Menu
						mode="inline"
						defaultSelectedKeys={["1"]}
						defaultOpenKeys={["sub1"]}
						style={{ height: "100%", borderRight: 0 }}
						theme="dark"
					>
						<SubMenu
							key="capex"
							icon={<ContainerOutlined />}
							title="Capex Forms"
						>
							<Menu.Item key="1" icon={<DashboardOutlined />}>
								Dashboard
								<Link to="/Capex/Dashboard" />
							</Menu.Item>
							<Menu.Item key="2" icon={<PlusOutlined />}>
								Raise New
								<Link to="/form/E3508ECC-7901-4552-B7DF-9734CB97403F" />
							</Menu.Item>
						</SubMenu>
						{/* <Menu.Item key="settings" icon={<SettingOutlined />}>
							Settings
						</Menu.Item> */}
					</Menu>
				</Sider>
				<AntdLayout>
					<Content className="site-content">{children}</Content>
				</AntdLayout>
			</AntdLayout>
		</AntdLayout>
	);
};
